import React from "react";
import { ErrorMessage, LoadingIndicator } from "@jsq/ds/core";
import { WorkflowMode } from "../../common/enums";
import { BaseFullScreenTakeover } from "../../Components/BaseFullScreenTakeover";
import { PaymentPrefConfirmation } from "../../Components/PaymentPrefConfirmation";
import { UpdatePositionsWithoutPaymentPrefPositionList } from "../../Components/PositionList/UpdatePositionsWithoutPaymentPrefPositionList";
import { UpdatePositionsWithPaymentPrefPositionList } from "../../Components/PositionList/UpdatePositionsWithPaymentPrefPositionList";
import { ConsolidatedPaymentPrefProvider } from "../../ConsolidatedPaymentPrefContextProvider";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { getSingleAvailablePaymentMethod } from "../../mappers";
import { BasePaymentPrefWorkflow } from "../BaseWorkflow";
import { usePaymentPrefCreationForAccountWorkflowController } from "./controller";
import { type PaymentPrefCreationForAccountWorkflowProps } from "./types";

/**
 * A workflow component that handles the creation of payment preferences at the account level.
 * It manages data fetching, form presentation, and position selection for creating new payment preferences.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string} props.accountId - ID of the account to create payment preferences for
 * @param {() => void} props.onExit - Function called when the workflow is exited
 * @param {() => void} props.onSuccess - Function called when the workflow completes successfully
 * @param {string} [props.sourcePaymentPrefId] - Optional ID of source payment preference for switching
 * @param {Arena} [props.arena] - Arena configuration object
 *
 * @example
 * ```tsx
 * <PaymentPrefCreationForAccountWorkflow
 *   accountId="123"
 *   onExit={() => setShowWorkflow(false)}
 *   onSuccess={handleSuccess}
 *   sourcePaymentPrefId="456"
 *   arena={{ features: { hasPositionPaymentPrefStaffApprovals: true }}}
 * />
 * ```
 *
 * @remarks
 * - Uses ConsolidatedPaymentPrefProvider for context management
 * - Handles loading and error states
 * - Supports selection of positions to update
 * - Integrates with BasePaymentPrefWorkflow for form presentation
 */
export const PaymentPrefCreationForAccountWorkflow: React.FC<
  PaymentPrefCreationForAccountWorkflowProps
> = ({ accountId, onExit, onSuccess, sourcePaymentPrefId, arena }) => {
  const {
    dataLoading,
    dataError,
    availablePaymentMethods,
    existingAccountPaymentPrefsData,
    associatePaymentPrefType,
    paymentPrefIdForContext,
    prefixComponents,
  } = usePaymentPrefCreationForAccountWorkflowController({
    accountId,
    sourcePaymentPrefId,
  });

  if (dataLoading) {
    return <LoadingIndicator />;
  }

  if (dataError) {
    return <ErrorMessage>Failed to load</ErrorMessage>;
  }

  const singleAvailablePaymentMethod = getSingleAvailablePaymentMethod(availablePaymentMethods);

  return (
    <ConsolidatedPaymentPrefProvider
      availablePaymentMethods={availablePaymentMethods}
      singleAvailablePaymentMethod={singleAvailablePaymentMethod}
      existingPaymentPrefs={existingAccountPaymentPrefsData?.account?.paymentPrefs}
      initialAssociatePaymentPref={associatePaymentPrefType}
      sourcePaymentPrefId={paymentPrefIdForContext}
      currentPosition={{ accountId, id: "", investmentEntityId: "", investmentEntityName: "" }}
      ListOfPositionsToUpdateComponent={
        associatePaymentPrefType ===
        AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
          ? UpdatePositionsWithPaymentPrefPositionList
          : UpdatePositionsWithoutPaymentPrefPositionList
      }
      workflowMode={WorkflowMode.Create}
      accountName={existingAccountPaymentPrefsData?.account?.name}
      arena={arena}
    >
      <BaseFullScreenTakeover title="Update payment instructions" onExit={onExit}>
        <BasePaymentPrefWorkflow
          workflowMode={WorkflowMode.Create}
          prefixComponents={prefixComponents}
          suffixComponents={[PaymentPrefConfirmation]}
          onExit={onExit}
          onSuccess={onSuccess}
        />
      </BaseFullScreenTakeover>
    </ConsolidatedPaymentPrefProvider>
  );
};

export type { PaymentPrefCreationForAccountWorkflowProps };
